import React, { useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faMousePointer,
  faLocationArrow,
  faPlay,
  faStop,
  faDirections,
} from '@fortawesome/pro-regular-svg-icons';

import {
  useAppDispatch,
  useAppSelector,
  isSelectingWaypointsSelector,
  setIsSelectingWaypoints,
  routeErrorSelector,
  setRouteError,
  isMapboxActiveSelector,
  showRouteSidebarSelector,
  setShowRouteSidebar,
  showCurrentLocationSelector,
  setShowCurrentLocation,
  isTrackingLocationSelector,
  setIsTrackingLocation,
  selectedWaypointIdsSelector,
} from '../../../store';

import './WayfindingControls.scss';

export const WayfindingControls: React.FC = () => {
  const dispatch = useAppDispatch();
  const isSelectingWaypoints = useAppSelector(isSelectingWaypointsSelector);
  const routeError = useAppSelector(routeErrorSelector);
  const isMapboxActive = useAppSelector(isMapboxActiveSelector);
  const showRouteSidebar = useAppSelector(showRouteSidebarSelector);
  const showCurrentLocation = useAppSelector(showCurrentLocationSelector);
  const isTrackingLocation = useAppSelector(isTrackingLocationSelector);
  const selectedWaypointIds = useAppSelector(selectedWaypointIdsSelector);

  const handleToggleLocation = useCallback(() => {
    dispatch(setShowCurrentLocation(!showCurrentLocation));
    dispatch(setIsTrackingLocation(!showCurrentLocation));
  }, [dispatch, showCurrentLocation]);

  // Automatically open sidebar when 2+ waypoints are selected
  useEffect(() => {
    if (selectedWaypointIds.length >= 2 && !showRouteSidebar) {
      dispatch(setShowRouteSidebar(true));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWaypointIds, dispatch]);

  if (!isMapboxActive) return null;

  return (
    <nav className="wayfinding-controls" aria-label="Wayfinding controls">
      <div className="waypoint-selection-controls">
        <button
          className={`selection-control-btn ${isSelectingWaypoints ? 'active' : ''}`}
          onClick={() => dispatch(setIsSelectingWaypoints(!isSelectingWaypoints))}
          title={isSelectingWaypoints ? 'Exit selection mode' : 'Select waypoints for routing'}
          aria-pressed={isSelectingWaypoints}
        >
          <FontAwesomeIcon icon={isSelectingWaypoints ? faTimes : faMousePointer} aria-hidden="true" />
          {isSelectingWaypoints ? 'Exit' : 'Select'}
        </button>

        <button
          className={`selection-control-btn route-planning-btn ${showRouteSidebar ? 'active' : ''}`}
          onClick={() => dispatch(setShowRouteSidebar(!showRouteSidebar))}
          title={showRouteSidebar ? 'Close route planning' : 'Open route planning'}
          aria-expanded={showRouteSidebar}
        >
          <FontAwesomeIcon icon={faDirections} aria-hidden="true" />
          Route Planning
        </button>

        <section className="gps-controls-section" aria-label="GPS location controls">
          <button
            className={`selection-control-btn gps-btn ${showCurrentLocation ? 'active' : ''}`}
            onClick={handleToggleLocation}
            title={showCurrentLocation ? 'Hide current location' : 'Show current location'}
            aria-pressed={showCurrentLocation}
          >
            <FontAwesomeIcon icon={faLocationArrow} aria-hidden="true" />
            {showCurrentLocation ? 'Hide Location' : 'Show Location'}
          </button>

          {showCurrentLocation && (
            <button
              className={`selection-control-btn tracking-btn ${isTrackingLocation ? 'active' : ''}`}
              onClick={() => dispatch(setIsTrackingLocation(!isTrackingLocation))}
              title={isTrackingLocation ? 'Stop live tracking' : 'Start live tracking'}
              aria-pressed={isTrackingLocation}
            >
              <FontAwesomeIcon icon={isTrackingLocation ? faStop : faPlay} aria-hidden="true" />
              {isTrackingLocation ? 'Stop Tracking' : 'Track Live'}
            </button>
          )}

        </section>

        {routeError && (
          <div className="route-error" role="alert" aria-live="assertive">
            {routeError}
            <button
              onClick={() => dispatch(setRouteError(null))}
              aria-label="Dismiss error message"
            >
              <FontAwesomeIcon icon={faTimes} aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};
